import { StyledGradientToggleButton } from "../styled/CustomComponent";
import { Box } from "@mui/material";
import ElectricEffects from "./ElectricEffects";
import AIEnergyIcon from "./AIEnergyIcon";
import { 
  smallButtonGradient,
  mediumButtonGradient,
  largeButtonGradient,
  smallButtonGradientHover,
  mediumButtonGradientHover,
  largeButtonGradientHover
} from "./electricAnimations";
import { useState, useEffect } from "react";

const AIButton = ({
  disabled = false,
  onClick,
  tooltipTitle,
  size = "medium",
  showSparks = true,
  showBurst = true,
  showElectricRing = true,
  sx = {},
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [energyParticles, setEnergyParticles] = useState([]);
  
  const sizeMap = {
    small: {
      width: "34px",
      height: "34px",
      iconSize: "small",
      gradient: smallButtonGradient,
      gradientHover: smallButtonGradientHover,
      shadow: "0 2px 8px hsl(var(--cyan) / 0.3)",
      particleCount: 8,
      particleSize: "2px",
    },
    medium: {
      width: "42px",
      height: "42px",
      iconSize: "medium",
      gradient: mediumButtonGradient,
      gradientHover: mediumButtonGradientHover,
      shadow: "0 4px 6px hsl(var(--cyan) / 0.35)",
      particleCount: 12,
      particleSize: "2.5px",
    },
    large: {
      width: "50px",
      height: "50px",
      iconSize: "large",
      gradient: largeButtonGradient,
      gradientHover: largeButtonGradientHover,
      shadow: "0 6px 10px hsl(var(--cyan) / 0.4)",
      particleCount: 16,
      particleSize: "3px",
    },
    xs: {
  width: "26px",
  height: "26px",
  iconSize: "small",
  gradient: smallButtonGradient,
  gradientHover: smallButtonGradientHover,
  shadow: "0 1px 4px hsl(var(--cyan) / 0.3)",
  particleCount: 6,
  particleSize: "1.5px",
},

  };

  const { width, height, iconSize, gradient, gradientHover, shadow, particleCount, particleSize } = sizeMap[size] || sizeMap.medium;

  const cyanColor = "hsl(var(--cyan))";
  const emeraldColor = "hsl(var(--emerald))";

  // Generate energy particles
  useEffect(() => {
    if (isHovered && !disabled) {
      const particles = Array.from({ length: particleCount }).map((_, i) => ({
        id: i,
        angle: (i * (360 / particleCount)) * (Math.PI / 180),
        distance: 15 + Math.random() * 10,
        size: 2 + Math.random() * 3,
        duration: 0.8 + Math.random() * 0.4,
        delay: Math.random() * 0.3,
        color: i % 2 === 0 ? cyanColor : emeraldColor,
      }));
      setEnergyParticles(particles);
    } else {
      setEnergyParticles([]);
    }
  }, [isHovered, disabled, particleCount]);

  const buttonStyles = {
    background: gradient,
    color: "white",
    border: "none",
    borderRadius: "50%",
    boxShadow: shadow,
    position: "relative",
    overflow: "visible",
    "&:hover": {
      background: gradientHover,
      transform: "scale(1.08)",
      "& .ai-energy-icon": {
        transform: "scale(1.15) translateY(-1px)",
        filter: `drop-shadow(0 0 2px ${cyanColor}) drop-shadow(0 0 1px ${emeraldColor})`,
      },
    },
    "&:active": {
      transform: "scale(0.98)",
    },
    transition: `all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
    width,
    height,
    minWidth: width,
    minHeight: height,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    ...sx,
  };

  return (
    <StyledGradientToggleButton
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={buttonStyles}
      {...props}
    >
      {/* AI Energy Particles Effect */}
      {isHovered && energyParticles.map((particle) => (
        <Box
          key={`particle-${particle.id}`}
          className="energy-particle"
          sx={{
            position: "absolute",
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: particle.color,
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: `translate(-50%, -50%) rotate(${particle.angle}rad)`,
            opacity: 0,
            zIndex: 1,
            boxShadow: `0 0 4px ${particle.color}, 0 0 8px ${particle.color}80`,
            filter: "blur(0.5px)",
            animation: `energy-emission ${particle.duration}s ease-out ${particle.delay}s`,
          }}
        />
      ))}

      {/* Subtle energy glow background */}
      {isHovered && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(
              circle at center,
              ${cyanColor}20 0%,
              transparent 70%
            )`,
            borderRadius: "50%",
            animation: "energy-glow-pulse 2s ease-in-out infinite",
            zIndex: 0,
          }}
        />
      )}

      {/* Greenish electric effects */}
      {showElectricRing && (
        <ElectricEffects 
          showSparks={showSparks}
          showBurst={showBurst}
          iconSize={iconSize}
          isHovered={isHovered}
        />
      )}
      
      {/* AI Energy Icon with energy pulse */}
      <Box sx={{ 
        position: "relative", 
        zIndex: 2,
        transition: "all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        transform: isHovered ? "scale(1.15) translateY(-1px)" : "scale(1)",
        filter: isHovered 
          ? `drop-shadow(0 0 2px ${cyanColor}) brightness(1.1)` 
          : `drop-shadow(0 0 3px ${cyanColor} / 0.7)`,
        // "& .ai-energy-icon": {
        //   animation: isHovered 
        //     ? "energy-pulse 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) infinite" 
        //     : "ai-icon-float 4s ease-in-out infinite",
        // }
      }}>
        <AIEnergyIcon 
          size={iconSize} 
          color="white"
          animate={false} // Disable float animation when hovered
          isHovered={isHovered}
        />
      </Box>

      {/* Energy ripple effect */}
      {isHovered && (
        <Box
          sx={{
            position: "absolute",
            inset: "-6px",
            borderRadius: "50%",
            border: `1px solid ${cyanColor}40`,
            animation: "energy-ripple 1.5s ease-out infinite",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}
    </StyledGradientToggleButton>
  );
};

export default AIButton;